import { useCallback } from 'react';

import { GroupedOption, SelectOption } from '~/shared/utils/types';
import { useXFetch } from '~/shared/utils/useXFetch';
import { VolumesAutoCompleteResponse } from '~/shared/types/api';

import { useSearchFilter } from './common/useSearchFilter';
import SearchFilterBase from './common/SearchFilterBase';
import SearchFilterOptionRow from './common/SearchFilterOptionRow';

export default function SearchInstance({
  filters,
  setFilters,
  isDisclosureOpen,
  accounts,
}: {
  filters: SelectOption[];
  setFilters: (selections: SelectOption[]) => void;
  isDisclosureOpen?: boolean;
  accounts?: string;
}) {
  const api = useXFetch();

  const searchOptions = useCallback(
    async (pattern: string): Promise<GroupedOption[]> => {
      const trimmedPattern = pattern.trim();

      if (!trimmedPattern) {
        return [];
      }

      try {
        const params = new URLSearchParams({ field: 'instanceId', pattern: trimmedPattern });
        if (accounts) params.set('accounts', accounts);
        const qs = params.toString();

        const response = await api<VolumesAutoCompleteResponse>(
          `/api/v1/volumes/autocomplete?${qs}`,
          { cache: 'no-store' }
        );
        const instanceIds = response.values || [];

        const groupedResults: GroupedOption[] =
          instanceIds.length > 0
            ? [
                {
                  label: 'Instance ID',
                  options: instanceIds.map((id) => ({
                    id,
                    name: id,
                    group: 'instanceId' as const,
                  })),
                },
              ]
            : [];

        return groupedResults;
      } catch (error) {
        console.error('[InstanceFilter] Error searching instances:', error);
        return [];
      }
    },
    [api, accounts]
  );

  const {
    searchResults,
    isLoading,
    inputValue,
    showResults,
    focusedIndex,
    localSelections,
    selectedIdSet,
    flattenedResults,
    inputRef,
    resultsRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleSelect,
    handleResultsMouseDown,
    handleResultsMouseUp,
    handleKeyDown,
  } = useSearchFilter({
    initialSelections: filters,
    onSelectionsChange: setFilters,
    searchFunction: searchOptions,
    filterGroup: 'instanceId',
    debounceMs: 300,
    defaultQuery: 'i-',
    isDisclosureOpen,
  });

  const renderOption = useCallback(
    (option: SelectOption, isSelected: boolean, isFocused: boolean = false) => (
      <SearchFilterOptionRow
        option={option}
        isSelected={isSelected}
        onSelect={handleSelect}
        tagVariant="instance"
        isFocused={isFocused}
      />
    ),
    [handleSelect]
  );

  return (
    <div className="flex w-full flex-1 flex-col">
      <SearchFilterBase
        inputValue={inputValue}
        onInputChange={handleInputChange}
        inputRef={inputRef}
        placeholder="Search"
        searchResults={searchResults}
        isLoading={isLoading}
        showResults={showResults}
        focusedIndex={focusedIndex}
        flattenedResults={flattenedResults}
        localSelections={localSelections}
        onSelect={handleSelect}
        selectedIdSet={selectedIdSet}
        renderOption={renderOption}
        className="w-full"
        mustTypeFirstMessage="Start typing instance id"
        resultsRef={resultsRef}
        onResultsMouseDown={handleResultsMouseDown}
        onResultsMouseUp={handleResultsMouseUp}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
      />
    </div>
  );
}
